import axios, { AxiosError, AxiosHeaders, AxiosResponse } from 'axios';
import { getToken, login } from '@/auth';
import store from '@/store';
import { translate } from '@/localization';
import { delay } from '@/utils/tools';

// Extend Axios config types to support our retry counter
declare module 'axios' {
  interface AxiosRequestConfig {
    _sechub_retry?: number;
  }
  interface InternalAxiosRequestConfig {
    _sechub_retry?: number;
  }
}

export const HEADER_REQUEST_ID = 'x-request-id';
const performedRequestsIds = new Set<string>();

// Retry configuration
const REQUEST_RETRIES = 3;
const RETRY_DELAY = 500;
const RETRY_CODES_RANGES: [number, number][] = [
  [400, 400], // Bad request
  [408, 408], // Request timeout
  [499, 499], // Client closed request
  [500, 599], // Server errors
];

// Create Axios instance
export const api = axios.create({
  baseURL: window.__env.VUE_APP_API_URL,
  headers: {
    'Content-Type': 'application/vnd.api+json',
    Accept: 'application/vnd.api+json',
    'Accept-Language': 'en',
  },
});

// Helper to check if a request ID was already performed
export function wasRequestPerformed(requestId: string): boolean {
  return performedRequestsIds.has(requestId);
}

// Dynamic header helpers
export function hasHeader(header: string): boolean {
  return (
    !!api.defaults.headers[header as keyof typeof api.defaults.headers] ||
    !!api.defaults.headers.common[
      header as keyof typeof api.defaults.headers.common
    ]
  );
}

export function setApiHeader(header: string, value: string): void {
  api.defaults.headers.common[header] = value;
}

export function removeApiHeader(header: string): void {
  delete api.defaults.headers.common[header];
}

// Request interceptor: attach fresh token and preserve GET content-type
api.interceptors.request.use(
  async (config) => {
    if (config.data === undefined) {
      config.data = null;
    }

    const token = await getToken();

    if (token) {
      config.headers = AxiosHeaders.from(config.headers);
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle JSON:API validation, retries, and 401
api.interceptors.response.use(
  (response) => {
    _validateJsonApi(response);

    const requestId = response.headers[HEADER_REQUEST_ID];
    if (requestId) {
      performedRequestsIds.add(requestId);
    }

    if (_dataMissing(response)) {
      store.$direct.notifications.dispatchNotify({
        type: 'error',
        message: translate('errors.dataMissing'),
      });
    }

    return response;
  },
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      await login();
      return Promise.reject(error);
    }
    return _retry(error);
  }
);

// — retry logic —
async function _retry(error: AxiosError): Promise<AxiosResponse> {
  const cfg = error.config as import('axios').InternalAxiosRequestConfig;
  if (!cfg) {
    return Promise.reject(error);
  }

  cfg._sechub_retry = cfg._sechub_retry ?? REQUEST_RETRIES;
  if (!_shouldRetry(error)) {
    return Promise.reject(error);
  }

  cfg._sechub_retry!--;
  await delay(RETRY_DELAY);
  return api.request(cfg);
}

function _shouldRetry(error: AxiosError): boolean {
  const cfg = error.config as import('axios').InternalAxiosRequestConfig;
  const retries = cfg._sechub_retry ?? 0;
  if (retries <= 0) {
    return false;
  }

  const status = error.response?.status;
  if (typeof status === 'number') {
    return RETRY_CODES_RANGES.some(
      ([min, max]) => status >= min && status <= max
    );
  }

  // Network errors (no status) are retried
  return true;
}

// — JSON:API validation —
function _validateJsonApi(response: AxiosResponse): void {
  const contentType =
    typeof response.headers['content-type'] === 'string' &&
    response.headers['content-type'].includes('application/vnd.api+json');

  if (import.meta.env.PROD || !contentType) {
    return;
  }

  const findDuplicates = (arr: any[]): Set<string> => {
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    arr.forEach((item) => {
      if (item && typeof item === 'object' && 'type' in item && 'id' in item) {
        const key = `${(item as any).type}:${(item as any).id}`;
        if (seen.has(key)) {
          duplicates.add(key);
        } else {
          seen.add(key);
        }
      }
    });
    return duplicates;
  };

  const dataArr = response.data?.data;
  if (Array.isArray(dataArr)) {
    const dups = findDuplicates(dataArr);
    if (dups.size) {
      console.error(
        `[API Response Validator] /data has duplicates: ${Array.from(dups).join(
          ', '
        )}`
      );
    }
  }

  const includedArr = response.data?.included;
  if (Array.isArray(includedArr)) {
    const dups = findDuplicates(includedArr);
    if (dups.size) {
      console.error(
        `[API Response Validator] /included has duplicates: ${Array.from(
          dups
        ).join(', ')}`
      );
    }
  }
}

function _dataMissing(response: AxiosResponse): boolean {
  const payload = response.data?.data;

  if (Array.isArray(payload)) {
    return payload.some(
      (rec: any) => rec.meta?.missing_required_fields?.length > 0
    );
  }

  if (payload && typeof payload === 'object') {
    return payload.meta?.missing_required_fields?.length > 0;
  }

  return false;
}
